export const COMPANY_NAME = "Odenberg Technologies";
export const WEBSITE_NAME = "Cocktail Bar";
export const WEBSITE_SLOGAN = "Digital menu & AI bartender";
export const COPYRIGHT = "Copyright © " + COMPANY_NAME;

export const TOOLBAR_TITLE = "Cocktail Bar";
export const TOOLBAR_DESCRIPTION = "Digital menu & AI bartender";

export const SIDEBAR_TITLE = "Navigate";
export const SIDEBAR_MENU = "Menu";
export const SIDEBAR_MENU_TOOLTIP = "Browse curated and full cocktail recipes";
export const SIDEBAR_CHAT = "AI Bartender";
export const SIDEBAR_CHAT_TOOLTIP = "Ask the AI for drink recommendations";
export const SIDEBAR_ADMIN = "Settings";
export const SIDEBAR_ADMIN_TOOLTIP = "Admin runtime settings";
export const SIDEBAR_LOGIN = "Login";
export const SIDEBAR_LOGIN_TOOLTIP = "Guest or admin password login";
export const SIDEBAR_PREFS = "Preferences";
export const SIDEBAR_PREFS_TOOLTIP = "Language and drink preferences";
export const SIDEBAR_FAVORITES = "Favorites";
export const SIDEBAR_FAVORITES_TOOLTIP = "Drinks you starred to try later";

export const BUTTON_SIGNIN = "Sign in";
export const BUTTON_SIGNOUT = "Sign out";
export const BUTTON_CANCEL = "Cancel";
export const BUTTON_BACK = "Back";
export const BUTTON_NEXT = "Next";

export const SNACKBAR_ERROR_SERVER = "Could not reach the server. Is the backend running?";
export const SNACKBAR_ERROR_RESPONSE = "Something went wrong.";
export const SNACKBAR_SUCCESS_LOGIN = "Signed in successfully.";
export const SNACKBAR_SUCCESS_SETTINGS = "Settings saved.";
export const SNACKBAR_SESSION_EXPIRED = "Please sign in again.";

export const LOGIN_TITLE = "Enter password";
export const LOGIN_GUEST = "Guest";
export const LOGIN_ADMIN = "Admin";
export const LOGIN_PASSWORD_LABEL = "Password";
export const LOGIN_SUBMIT = "Continue";

export const MENU_CURATED = "Curated menu";
export const MENU_FULL = "Full library";
export const MENU_FILTERS = "Filters";
export const MENU_SEARCH = "Search cocktails…";
export const MENU_READY = "Ready to make";
export const MENU_SHOW_RATING = "Show ratings";
export const MENU_BASE = "Base spirit";
export const MENU_BASE_ANY = "Any";
export const MENU_INGREDIENTS = "Ingredients";
export const MENU_RATING = "Rating";
export const MENU_EMPTY = "No cocktails match your filters.";
export const MENU_LOADING = "Loading menu…";
export const MENU_ACCESS_REQUIRED_TITLE = "Sign in required";
export const MENU_ACCESS_REQUIRED_BODY =
    "This cocktail menu is password-protected. Sign in as a guest to browse drinks and chat with the bartender.";
export const MENU_FORBIDDEN_TITLE = "Access denied";
export const MENU_FORBIDDEN_BODY =
    "Your current sign-in cannot open the menu. Sign in again as a guest, or ask the host to open public access.";
export const MENU_UNAVAILABLE_TITLE = "Menu temporarily unavailable";
export const MENU_UNAVAILABLE_BODY =
    "We couldn't load recipes from the bar sheet right now. Try again in a moment.";
export const MENU_LOAD_ERROR_TITLE = "Couldn't load the menu";
export const MENU_LOAD_ERROR_BODY = "Something went wrong while fetching the menu. Check your connection or try again.";
export const MENU_RETRY = "Try again";
export const MENU_GO_LOGIN = "Sign in";
export const MENU_ASK_BARTENDER = "Ask the bartender";
export const MENU_FAVORITE = "Save to favorites";
export const MENU_UNFAVORITE = "Remove from favorites";
export const MENU_SURPRISE_ME = "Surprise me";
export const MENU_SURPRISE_EMPTY = "No ready drinks on the curated menu match your preferences.";
export const FAVORITES_TITLE = "Favorites";
export const FAVORITES_SUBTITLE = "Drinks you've starred.";
export const FAVORITES_EMPTY = "No favorites yet. Star a cocktail on the menu to save it here.";
export const MENU_PAGE_SIZE = "Per page";
export const MENU_PAGE_PREV = "Back";
export const MENU_PAGE_NEXT = "Next";
export const menuPageOf = (page, pageCount) => `Page ${page} of ${pageCount}`;
export const menuPageRange = (from, to, total) => `${from}–${to} of ${total}`;
export const MENU_CHIP_READY = "Ready";
export const MENU_CHIP_ON_MENU = "On menu";
export const MENU_INSTRUCTIONS = "Instructions";
export const MENU_NOTES = "Notes";
export const MENU_CLEAR_SEARCH = "Clear search";
export const MENU_CLOSE = "Close";

export const CHAT_TITLE = "AI Bartender";
export const CHAT_PLACEHOLDER = "Ask for a recommendation…";
export const CHAT_SEND = "Send";
export const CHAT_CLEAR = "Clear chat";
export const CHAT_EDIT = "Edit";
export const CHAT_RETRY = "Retry";
export const CHAT_EDIT_HINT = "Edit your last message";
export const CHAT_RETRY_HINT = "Send your last message again";
export const CHAT_EMPTY = "I can help you find what to drink — give me a description of what you like and I'll recommend from the bar menu.";
export const CHAT_THINKING = "Thinking…";
/** Rotating status lines while waiting for the first stream token. */
export const CHAT_THINKING_LINES = [
    "Polishing the shaker…",
    "Consulting the ice…",
    "Sniffing the citrus…",
    "Flipping through the recipe book…",
    "Checking what's in stock…",
    "Stirring up a plan…",
    "Wiping down the bar top…",
    "Warming up the jigger…",
    "Asking the olives for a second opinion…",
    "Counting dashes of bitters…",
    "Chilling the coupe…",
    "Tasting the air for inspiration…",
    "Finding the right glass…",
    "Shaking out bad ideas…",
    "Negotiating with the mint…",
    "Looking for the good stuff…",
    "Calibrating the pour…",
    "Dusting off a classic…",
    "Thinking…"
];
export const chatDrinkPrompt = (drinkName) =>
    `I'm looking at ${drinkName} on the menu. Tell me about it — history, variations, and whether it would suit me.`;

/** Auto-sent when a guest leaves Welcome and opens the AI bartender. */
export const chatWelcomeRecommendPrompt = ({ drinkTypeLabel, tasteLabels }) => {
    return (
        `I just chose my preferences. Please recommend a few drinks from the menu that would suit me. ` +
        `Prefer options that are ready to make.`
    );
};

export const ADMIN_TITLE = "Admin settings";
export const ADMIN_AUTH_ENABLED = "Require password login";
export const ADMIN_PUBLIC_ACCESS = "Temporary public access";
export const ADMIN_PUBLIC_HOURS = "Open publicly for (hours)";
export const ADMIN_PUBLIC_UNTIL = "Public access until";
export const ADMIN_VENUE = "Default venue";
export const ADMIN_VENUE_HELP =
    "Used when a guest has not chosen a venue in Preferences. Guests can override this per device.";
export const ADMIN_SAVE = "Save settings";
export const ADMIN_DENIED = "Admin access required.";
export const ADMIN_IMAGES_TITLE = "Cocktail images";
export const ADMIN_IMAGES_HELP =
    "Upload a Google Sheets web export (.zip). We parse Recipes.html, match drink names to cellImage files, and cache them for the menu.";
export const ADMIN_IMAGES_UPLOAD = "Upload Sheets export zip";
export const ADMIN_IMAGES_IMPORT_FOLDER = "Import from server folder";
export const ADMIN_IMAGES_RESULT = "Import result";
export const adminImagesImported = (imported, mapped) => `Imported ${imported}/${mapped} cocktail images`;
export const ADMIN_IMAGES_CACHED = "cached";
export const ADMIN_MENU_TITLE = "Menu data";
export const ADMIN_MENU_HELP =
    "Recipes are cached from Google Sheets. Refresh after you edit the spreadsheet to pull the latest data without waiting for the cache to expire.";
export const ADMIN_MENU_REFRESH = "Refresh from Google Sheets";
export const ADMIN_MENU_EXPORT = "Copy menu export to clipboard";
export const ADMIN_MENU_EXPORT_HELP =
    "Copies the full recipe list and which ingredients are in or out of stock for the default venue above — handy to paste into another chatbot.";
export const SNACKBAR_SUCCESS_MENU_REFRESH = "Menu refreshed from Google Sheets.";
export const SNACKBAR_SUCCESS_MENU_EXPORT = "Menu export copied to clipboard.";
export const SNACKBAR_ERROR_MENU_EXPORT = "Could not copy menu export.";

export const LANGUAGE_LABEL = "Language";

export const PREFS_WELCOME_SUBTITLE = "Choose your language, venue, and what you'd like to browse.";
export const PREFS_EDIT_SUBTITLE = "Update your language, drinks, tastes, and venue.";
export const PREFS_LANGUAGE = "Language";
export const PREFS_VENUE = "Venue";
export const PREFS_VENUE_HINT = "Which home bar’s curated menu and stock should we use?";
export const PREFS_DRINK_TYPE = "What would you like to drink?";
export const PREFS_ALCOHOLIC = "Alcoholic cocktails";
export const PREFS_NON_ALCOHOLIC = "Non-alcoholic cocktails";
export const PREFS_TASTES = "Taste leanings";
export const PREFS_TASTES_HINT = "Optional — pick any that sound good. The bartender will keep these in mind.";
export const PREFS_TASTE_BITTER = "Bitter";
export const PREFS_TASTE_TART = "Tart / sour";
export const PREFS_TASTE_SWEET = "Sweet";
export const PREFS_TASTE_FRUITY = "Fruity";
export const PREFS_TASTE_CITRUSY = "Citrusy";
export const PREFS_TASTE_HERBAL = "Herbal";
export const PREFS_TASTE_FLORAL = "Floral";
export const PREFS_TASTE_CREAMY = "Creamy";
export const PREFS_TASTE_NUTTY = "Nutty";
export const PREFS_TASTE_SPICY = "Spicy";
export const PREFS_TASTE_REFRESHING = "Refreshing";
export const PREFS_TASTE_BOOZY = "Boozy";
export const PREFS_TASTE_LIGHT = "Light";
export const PREFS_TASTE_DRY = "Dry";
export const PREFS_TASTE_SMOKY = "Smoky";
export const PREFS_TASTE_TROPICAL = "Tropical";
export const PREFS_TASTE_BUBBLY = "Bubbly";
export const PREFS_CONTINUE = "Continue to the menu";
export const PREFS_GO_MENU = "Browse the menu";
export const PREFS_ASK_BARTENDER = "Ask the AI bartender";
export const PREFS_SAVE = "Save preferences";
export const MENU_DRINK_TYPE = "Drinks";
export const MENU_DRINK_TYPE_ALCOHOLIC = "Showing alcoholic";
export const MENU_DRINK_TYPE_NON_ALCOHOLIC = "Showing non-alcoholic";

export const MENU_TOUR_NEXT = "Next";
export const MENU_TOUR_DONE = "Got it";
export const MENU_TOUR_SKIP = "Skip tour";
export const MENU_TOUR_STEP = (current, total) => `${current} / ${total}`;
export const MENU_TOUR_VIEW_TITLE = "Curated vs full library";
export const MENU_TOUR_VIEW_BODY =
    "The curated menu shows special picks for this venue. Switch to the full library to browse every recipe.";
export const MENU_TOUR_SURPRISE_TITLE = "Surprise me";
export const MENU_TOUR_SURPRISE_BODY =
    "Feeling indecisive? This picks a random ready drink from the curated menu.";
export const MENU_TOUR_FILTERS_TITLE = "Filters";
export const MENU_TOUR_FILTERS_BODY =
    "Open filters to search, switch alcoholic / non-alcoholic, pick a base spirit, ingredients, and ready-to-make.";
export const MENU_TOUR_FAVORITE_TITLE = "Favorites";
export const MENU_TOUR_FAVORITE_BODY =
    "Tap the star on any drink to save it. Find your list anytime under Favorites in the sidebar.";
export const MENU_TOUR_ASK_TITLE = "Ask the bartender";
export const MENU_TOUR_ASK_BODY =
    "Tap the sparkle to ask the AI bartender about that drink — history, variations, and whether it suits you.";
